import { useState, type ReactElement } from 'react';
import { useForm, type FieldValues } from 'react-hook-form';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Center,
  Code,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Popover,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconEye,
  IconEyeOff,
  IconGrid3x3,
  IconLayoutGrid,
  IconPlus,
  IconRestore,
  IconTrash,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { FormLayoutRenderer } from './FormLayoutRenderer';
import {
  findUnassignedFieldNames,
  type FieldRegistry,
  type LayoutDocument,
  type LayoutFieldEntry,
  type LayoutGroup,
} from './types';
import { useAdminProjectsList } from '../../queries/admin-projects';
import { ApiError } from '../api-error';

interface ResolvedLayout<TName extends string> {
  layout: LayoutDocument<TName>;
  scope: 'global' | 'project' | 'default';
  resolved_from?: 'project' | 'global' | 'none' | undefined;
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export interface LayoutEditorShellProps<TSchema extends FieldValues, TName extends string, TContext> {
  /** Title at the top of the page. */
  title: string;
  /** Breadcrumb label for the AdminHub link. */
  breadcrumbLabel: string;

  registry: FieldRegistry<TSchema, TName, TContext>;
  allNames: readonly TName[];
  defaultLayout: LayoutDocument<TName>;

  /** Hook returning the active layout for (scope, projectId). */
  useActive: (
    scope: 'global' | 'project',
    projectId: string | null,
  ) => UseQueryResult<ResolvedLayout<TName>>;

  /** Hook for the save mutation. */
  useUpdate: () => UseMutationResult<
    ResolvedLayout<TName>,
    Error,
    {
      scope: 'global' | 'project';
      project_id?: string | null;
      layout: LayoutDocument<TName>;
      notes?: string | null;
    }
  >;

  /** Default values for the preview form. The renderer's `useWatch` reads
   *  these to drive `visibleWhen`. Pre-populate any conditional triggers
   *  (e.g. has_mineral_reservation: true) so admins see the conditional
   *  fields in the preview. */
  previewDefaults: TSchema;

  /** Static context bag for the preview's field renders (e.g., docTypes). */
  previewContext: TContext;
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const next = [...arr];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

function uniqueGroupId(existing: string[]): string {
  let n = 1;
  while (existing.includes(`group_${n}`)) n += 1;
  return `group_${n}`;
}

export function LayoutEditorShell<
  TSchema extends FieldValues,
  TName extends string,
  TContext,
>(props: LayoutEditorShellProps<TSchema, TName, TContext>): ReactElement {
  const {
    title,
    breadcrumbLabel,
    registry,
    allNames,
    defaultLayout,
    useActive,
    useUpdate,
    previewDefaults,
    previewContext,
  } = props;

  const [scope, setScope] = useState<'global' | 'project'>('global');
  const [projectId, setProjectId] = useState<string | null>(null);
  const projectsQ = useAdminProjectsList({ limit: 200 });
  const activeQ = useActive(scope, projectId);
  const updateM = useUpdate();

  const [draft, setDraft] = useState<LayoutDocument<TName> | null>(null);
  const [lastServerLayout, setLastServerLayout] = useState<LayoutDocument<TName> | null>(null);
  const [notes, setNotes] = useState('');

  // Adjust state during render — pull the server layout into the draft on
  // first arrival and on every scope/project change.
  const serverLayout = activeQ.data?.layout ?? null;
  if (serverLayout !== lastServerLayout) {
    setLastServerLayout(serverLayout);
    if (serverLayout) setDraft(serverLayout);
  }

  // When the user has flipped to scope='project' but hasn't picked a project
  // yet, the query is disabled (we can't ask "active layout for project null").
  // Seed the draft with the built-in default so the editor renders and the
  // project picker is reachable. As soon as they pick a project, the active
  // query fires and the block above replaces the draft with that project's
  // layout (or with the default if the project has none).
  const needsProject = scope === 'project' && !projectId;
  if (draft === null && needsProject) {
    setDraft(defaultLayout);
  }

  // Throwaway form for the preview pane. Cast through `unknown` because
  // react-hook-form's DefaultValues<TSchema> uses a recursive Partial that
  // TS can't unify with an arbitrary `TSchema extends FieldValues`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const previewForm = useForm<TSchema>({ defaultValues: previewDefaults as any });

  if (activeQ.isLoading || !draft) {
    return (
      <Center h="60vh">
        <Loader />
      </Center>
    );
  }

  const groupIds = draft.groups.map((g) => g.id);
  const unassigned = findUnassignedFieldNames(draft, allNames);

  function addGroup() {
    setDraft({
      ...draft!,
      groups: [
        ...draft!.groups,
        {
          id: uniqueGroupId(groupIds),
          title: 'New group',
          collapsible: false,
          default_open: true,
          fields: [],
        },
      ],
    });
  }

  function moveGroup(idx: number, dir: -1 | 1) {
    setDraft({ ...draft!, groups: moveItem(draft!.groups, idx, idx + dir) });
  }

  function patchGroup(idx: number, patch: Partial<LayoutGroup<TName>>) {
    setDraft({
      ...draft!,
      groups: draft!.groups.map((g, i) => (i === idx ? { ...g, ...patch } : g)),
    });
  }

  function deleteGroup(idx: number) {
    const g = draft!.groups[idx]!;
    if (g.fields.length > 0) {
      notifications.show({
        title: 'Move fields out first',
        message: `Group "${g.title}" still has ${g.fields.length} field(s). Move them to another group or to Unassigned before deleting.`,
        color: 'orange',
      });
      return;
    }
    setDraft({ ...draft!, groups: draft!.groups.filter((_, i) => i !== idx) });
  }

  function moveField(groupIdx: number, fieldIdx: number, dir: -1 | 1) {
    setDraft({
      ...draft!,
      groups: draft!.groups.map((g, i) =>
        i === groupIdx ? { ...g, fields: moveItem(g.fields, fieldIdx, fieldIdx + dir) } : g,
      ),
    });
  }

  function patchField(groupIdx: number, fieldIdx: number, patch: Partial<LayoutFieldEntry<TName>>) {
    setDraft({
      ...draft!,
      groups: draft!.groups.map((g, i) =>
        i === groupIdx
          ? {
              ...g,
              fields: g.fields.map((f, j) => (j === fieldIdx ? { ...f, ...patch } : f)),
            }
          : g,
      ),
    });
  }

  function moveFieldToGroup(
    fromGroupIdx: number,
    fieldIdx: number,
    toGroupId: string | 'unassigned',
  ) {
    const fromGroup = draft!.groups[fromGroupIdx]!;
    const field = fromGroup.fields[fieldIdx]!;
    const remaining = fromGroup.fields.filter((_, i) => i !== fieldIdx);
    let nextGroups = draft!.groups.map((g, i) =>
      i === fromGroupIdx ? { ...g, fields: remaining } : g,
    );
    if (toGroupId !== 'unassigned') {
      nextGroups = nextGroups.map((g) =>
        g.id === toGroupId ? { ...g, fields: [...g.fields, field] } : g,
      );
    }
    setDraft({ ...draft!, groups: nextGroups });
  }

  function placeUnassigned(name: TName, toGroupId: string) {
    const desc = registry[name];
    setDraft({
      ...draft!,
      groups: draft!.groups.map((g) =>
        g.id === toGroupId ? { ...g, fields: [...g.fields, { name, span: desc.defaultSpan }] } : g,
      ),
    });
  }

  function resetToActive() {
    if (activeQ.data) setDraft(activeQ.data.layout);
  }

  function resetToDefault() {
    modals.openConfirmModal({
      title: 'Reset to built-in default?',
      children: (
        <Text size="sm">
          Replaces your draft with the original layout shipped in the app. Won't save until you click
          Save.
        </Text>
      ),
      labels: { confirm: 'Reset draft', cancel: 'Cancel' },
      onConfirm: () => setDraft(defaultLayout),
    });
  }

  function save() {
    if (scope === 'project' && !projectId) {
      notifications.show({ title: 'Pick a project first', message: '', color: 'orange' });
      return;
    }
    updateM.mutate(
      {
        scope,
        project_id: scope === 'project' ? projectId : null,
        layout: draft!,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Layout saved',
            message:
              scope === 'project'
                ? 'Members of this project will see the new layout.'
                : 'Every user will see the new layout next time they open the form.',
            color: 'pine',
          });
        },
        onError: (e: Error) =>
          notifications.show({
            title: 'Could not save layout',
            message: e instanceof ApiError ? e.message : e.message,
            color: 'red',
          }),
      },
    );
  }

  return (
    <Container size="xl">
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor component={Link} to="/admin">
            Admin
          </Anchor>
          <Text>{breadcrumbLabel}</Text>
        </Breadcrumbs>

        <Group justify="space-between" align="flex-end">
          <Stack gap={4}>
            <Group gap="xs">
              <IconLayoutGrid size={20} />
              <Title order={2}>{title}</Title>
              <Badge variant="light" color={activeQ.data?.scope === 'default' ? 'gray' : 'pine'}>
                {activeQ.data?.scope === 'default' ? 'Default' : 'Customized'}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Rearrange / resize / regroup fields. Saved layouts apply globally OR per-project,
              depending on the scope picker.
            </Text>
          </Stack>
          <Group gap="xs">
            <Button
              variant="subtle"
              leftSection={<IconRestore size={14} />}
              onClick={resetToActive}
              disabled={!activeQ.data}
            >
              Discard changes
            </Button>
            <Button
              variant="default"
              leftSection={<IconGrid3x3 size={14} />}
              onClick={resetToDefault}
            >
              Reset to default
            </Button>
            <Button onClick={save} loading={updateM.isPending}>
              Save
            </Button>
          </Group>
        </Group>

        <Card withBorder padding="sm" radius="md">
          <Group gap="md" align="flex-end" wrap="wrap">
            <Stack gap={4}>
              <Text size="xs" fw={500}>
                Scope
              </Text>
              <SegmentedControl
                value={scope}
                onChange={(v) => {
                  const s = v as 'global' | 'project';
                  setScope(s);
                  setDraft(null);
                  setLastServerLayout(null);
                }}
                data={[
                  { value: 'global', label: 'Global (everyone)' },
                  { value: 'project', label: 'This project' },
                ]}
              />
            </Stack>
            {scope === 'project' && (
              <Select
                label="Project"
                placeholder="Pick a project"
                searchable
                clearable
                w={300}
                data={(projectsQ.data?.projects ?? []).map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
                value={projectId}
                onChange={(v) => {
                  setProjectId(v);
                  setDraft(null);
                  setLastServerLayout(null);
                }}
              />
            )}
            <Text size="xs" c="dimmed">
              {scope === 'global'
                ? 'Edits here apply to every user.'
                : projectId
                  ? `Edits apply only to project members. Falls back to global if not set.`
                  : 'Pick a project to edit its layout.'}
            </Text>
          </Group>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="md">
              <Card withBorder padding="sm" radius="md">
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Groups
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={12} />}
                    onClick={addGroup}
                  >
                    Add group
                  </Button>
                </Group>
                <Stack gap="sm" mt="sm">
                  {draft.groups.map((g, gi) => (
                    <GroupBlock
                      key={g.id}
                      group={g}
                      registry={registry}
                      isFirst={gi === 0}
                      isLast={gi === draft.groups.length - 1}
                      otherGroupIds={groupIds.filter((id) => id !== g.id)}
                      onMoveGroup={(dir) => moveGroup(gi, dir)}
                      onPatchGroup={(patch) => patchGroup(gi, patch)}
                      onDeleteGroup={() => deleteGroup(gi)}
                      onMoveField={(fi, dir) => moveField(gi, fi, dir)}
                      onPatchField={(fi, patch) => patchField(gi, fi, patch)}
                      onMoveFieldToGroup={(fi, toId) => moveFieldToGroup(gi, fi, toId)}
                    />
                  ))}
                </Stack>
              </Card>

              {unassigned.length > 0 && (
                <UnassignedBlock
                  fields={unassigned}
                  registry={registry}
                  groupIds={groupIds}
                  onPlace={placeUnassigned}
                />
              )}

              <Card withBorder padding="sm" radius="md">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Save notes (optional)
                  </Text>
                  <Textarea
                    placeholder="What changed? (Stored on the audit row for future reference.)"
                    autosize
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.currentTarget.value)}
                  />
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="sm">
                <IconEye size={16} />
                <Text size="sm" fw={500}>
                  Live preview
                </Text>
                <Text size="xs" c="dimmed">
                  Reflects every change. Inputs are disabled.
                </Text>
              </Group>
              <Divider mb="sm" />
              <FormLayoutRenderer
                layout={draft}
                control={previewForm.control}
                register={previewForm.register}
                registry={registry}
                allNames={allNames}
                context={previewContext}
                disabled
              />
            </Card>
          </Grid.Col>
        </Grid>

        <Alert color="blue" variant="light">
          Looking for the deferred save-time hidden-field guard or one-click revert? Those are
          tracked as v1.1 follow-ups; the data model already supports them.
        </Alert>
      </Stack>
    </Container>
  );
}

/* ------------------------------------------------------------------ */

interface GroupBlockProps<TSchema extends FieldValues, TName extends string, TContext> {
  group: LayoutGroup<TName>;
  registry: FieldRegistry<TSchema, TName, TContext>;
  isFirst: boolean;
  isLast: boolean;
  otherGroupIds: string[];
  onMoveGroup: (dir: -1 | 1) => void;
  onPatchGroup: (patch: Partial<LayoutGroup<TName>>) => void;
  onDeleteGroup: () => void;
  onMoveField: (fieldIdx: number, dir: -1 | 1) => void;
  onPatchField: (fieldIdx: number, patch: Partial<LayoutFieldEntry<TName>>) => void;
  onMoveFieldToGroup: (fieldIdx: number, toId: string | 'unassigned') => void;
}

function GroupBlock<TSchema extends FieldValues, TName extends string, TContext>(
  props: GroupBlockProps<TSchema, TName, TContext>,
) {
  const {
    group,
    registry,
    isFirst,
    isLast,
    otherGroupIds,
    onMoveGroup,
    onPatchGroup,
    onDeleteGroup,
    onMoveField,
    onPatchField,
    onMoveFieldToGroup,
  } = props;
  return (
    <Paper withBorder p="sm" radius="sm">
      <Stack gap="xs">
        <Group gap="xs">
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => onMoveGroup(-1)}
            disabled={isFirst}
            aria-label="Move group up"
          >
            <IconArrowUp size={14} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => onMoveGroup(1)}
            disabled={isLast}
            aria-label="Move group down"
          >
            <IconArrowDown size={14} />
          </ActionIcon>
          <TextInput
            value={group.title}
            onChange={(e) => onPatchGroup({ title: e.currentTarget.value })}
            style={{ flex: 1 }}
          />
          <Switch
            label="Collapsible"
            size="xs"
            checked={group.collapsible ?? false}
            onChange={(e) => onPatchGroup({ collapsible: e.currentTarget.checked })}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={onDeleteGroup}
            aria-label="Delete group"
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>

        {group.fields.length === 0 ? (
          <Text size="xs" c="dimmed" pl="md">
            (empty group — drag fields here from Unassigned, or move them from another group)
          </Text>
        ) : (
          <Stack gap={6} pl="md">
            {group.fields.map((f, fi) => (
              <FieldChip
                key={f.name}
                field={f}
                registry={registry}
                isFirst={fi === 0}
                isLast={fi === group.fields.length - 1}
                otherGroupIds={otherGroupIds}
                onMove={(dir) => onMoveField(fi, dir)}
                onPatch={(patch) => onPatchField(fi, patch)}
                onMoveToGroup={(toId) => onMoveFieldToGroup(fi, toId)}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

interface FieldChipProps<TSchema extends FieldValues, TName extends string, TContext> {
  field: LayoutFieldEntry<TName>;
  registry: FieldRegistry<TSchema, TName, TContext>;
  isFirst: boolean;
  isLast: boolean;
  otherGroupIds: string[];
  onMove: (dir: -1 | 1) => void;
  onPatch: (patch: Partial<LayoutFieldEntry<TName>>) => void;
  onMoveToGroup: (toId: string | 'unassigned') => void;
}

function FieldChip<TSchema extends FieldValues, TName extends string, TContext>(
  props: FieldChipProps<TSchema, TName, TContext>,
) {
  const { field, registry, isFirst, isLast, otherGroupIds, onMove, onPatch, onMoveToGroup } = props;
  const desc = registry[field.name];
  if (!desc) {
    return (
      <Group gap="xs" wrap="nowrap">
        <Code>{field.name}</Code>
        <Badge color="orange" variant="light">
          unknown — will be ignored at render time
        </Badge>
      </Group>
    );
  }
  return (
    <Paper withBorder p={6} radius="xs" bg="var(--mantine-color-default-hover)">
      <Group gap="xs" wrap="nowrap">
        <ActionIcon
          variant="subtle"
          size="xs"
          onClick={() => onMove(-1)}
          disabled={isFirst}
          aria-label="Move field up"
        >
          <IconArrowUp size={12} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          size="xs"
          onClick={() => onMove(1)}
          disabled={isLast}
          aria-label="Move field down"
        >
          <IconArrowDown size={12} />
        </ActionIcon>
        <Text size="sm" style={{ flex: 1 }}>
          <strong>{desc.label}</strong>{' '}
          <Text span size="xs" c="dimmed">
            <Code>{field.name}</Code>
          </Text>
        </Text>
        <SpanPopover value={field.span} onChange={(span) => onPatch({ span })} />
        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={() => onPatch({ hidden: !field.hidden })}
          aria-label={field.hidden ? 'Show field' : 'Hide field'}
          color={field.hidden ? 'red' : 'gray'}
        >
          {field.hidden ? <IconEyeOff size={14} /> : <IconEye size={14} />}
        </ActionIcon>
        <Select
          size="xs"
          placeholder="Move to…"
          w={140}
          data={[
            ...otherGroupIds.map((id) => ({ value: id, label: id })),
            { value: 'unassigned', label: '↓ Unassigned' },
          ]}
          value={null}
          onChange={(v) => v && onMoveToGroup(v as string | 'unassigned')}
        />
      </Group>
    </Paper>
  );
}

function SpanPopover({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [opened, setOpened] = useState(false);
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom" withArrow>
      <Popover.Target>
        <Badge variant="light" style={{ cursor: 'pointer' }} onClick={() => setOpened((v) => !v)}>
          span {value}
        </Badge>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs" w={220}>
          <Text size="xs">Width on desktop (out of 12)</Text>
          <Slider
            value={value}
            onChange={(v) => onChange(v)}
            min={1}
            max={12}
            step={1}
            marks={[
              { value: 3, label: '3' },
              { value: 6, label: '6' },
              { value: 12, label: '12' },
            ]}
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

interface UnassignedBlockProps<TSchema extends FieldValues, TName extends string, TContext> {
  fields: TName[];
  registry: FieldRegistry<TSchema, TName, TContext>;
  groupIds: string[];
  onPlace: (name: TName, toGroupId: string) => void;
}

function UnassignedBlock<TSchema extends FieldValues, TName extends string, TContext>(
  props: UnassignedBlockProps<TSchema, TName, TContext>,
) {
  const { fields, registry, groupIds, onPlace } = props;
  return (
    <Paper withBorder p="sm" radius="sm" bg="var(--mantine-color-yellow-light)">
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Unassigned fields ({fields.length})
        </Text>
        <Text size="xs" c="dimmed">
          These fields exist on the form but aren't placed in any group. Pick a group to add them.
          Until placed, they render in a sticky "Unassigned" group at the bottom of the form for
          everyone.
        </Text>
        {fields.map((name) => (
          <Group key={name} gap="xs" wrap="nowrap">
            <Text size="sm" style={{ flex: 1 }}>
              <strong>{registry[name].label}</strong>{' '}
              <Text span size="xs" c="dimmed">
                <Code>{name}</Code>
              </Text>
            </Text>
            <Select
              size="xs"
              placeholder="Place in…"
              w={160}
              data={groupIds.map((id) => ({ value: id, label: id }))}
              value={null}
              onChange={(v) => v && onPlace(name, v)}
            />
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}

